// A refused ask, turned into the words a person reads
// (bead hf-e6-commitments-coverage-udt.1).
//
// ── ONE REFUSAL VOCABULARY, EXTENDED RATHER THAN REPLACED ──────────────────────
//
// `RequestRejection` is `CommitmentRejection` plus three members that only a QUESTION
// can produce. So this delegates the shared ones to `../claim`'s `messageFor` and
// answers only the three, rather than writing a second total mapping.
//
// That matters most for the late yes. `capacity_full` from an accepted ask is the
// SAME event as `capacity_full` from a volunteer tap — two people were kind at the
// same moment — and it gets the same reviewed sentence, "Someone else just stepped
// in". A warmer variant written here would be two sentences for one moment, and the
// two would drift until a person's experience depended on which button they pressed.
//
// IT IS TOTAL ON PURPOSE. TypeScript will not let a new rejection kind slip through
// as an unhandled case, because the alternative is somebody seeing a blank panel — or
// a raw code — at the exact moment they were trying to answer honestly.

import type { RequestRejection } from "@herefor/contracts";
import { DIRECT_REQUEST_MESSAGES as COPY, type ActionableMessage } from "@herefor/product-language";

import { messageFor } from "../claim";

/**
 * The message for any refusal an ask can produce.
 *
 * `displayName` is the person the ask was ABOUT. It is needed only by the two
 * organiser-side refusals that name somebody, and its absence is handled rather than
 * asserted: a surface that has the rejection but not the name still gets a true
 * sentence instead of "undefined has already been asked".
 */
export function requestMessageFor(
  rejection: RequestRejection,
  displayName?: string,
): ActionableMessage {
  switch (rejection.kind) {
    case "already_asked":
      return displayName === undefined
        ? COPY.refusals.noLongerOpen
        : COPY.refusals.alreadyAsked(displayName);
    case "person_not_reachable":
      return displayName === undefined
        ? COPY.refusals.noLongerOpen
        : COPY.refusals.notReachable(displayName);
    // Nothing to do with WHO was asked, so it names nobody. The sentence is a signpost
    // to the two paths that work once a window is open (bead hf-gzv).
    case "help_already_started":
      return COPY.refusals.alreadyStarted;
    case "request_not_open":
      return COPY.refusals.noLongerOpen;
    // Every shared kind — including the late yes — is answered by the one mapping the
    // volunteer and recheck flows already use. Listed by name rather than caught by a
    // `default`, so a new rejection in the contract is a COMPILE error here instead of
    // quietly falling through to somebody else's sentence.
    case "not_a_member":
    case "missing_capability":
    case "circle_not_found":
    case "not_found":
    case "validation_failed":
    case "revision_stale":
    case "empty_window":
    case "crosses_day_boundary":
    case "capacity_full":
    case "places_awaiting_recheck":
    case "needs_recheck":
    case "already_committed":
    case "not_responsible":
    case "need_not_open":
    case "conflicts_with_commitment":
      return messageFor(rejection);
  }
}
